export class Tooltip {
  /**
   * Represents a tooltip component
   *
   * Uses Custom Element <tooltip-info>
   *
   * @constructor
   * @param {HTMLElement} container - parent custom element
   * @property {HTMLElement} tooltipContent - the content that appears as the tooltip
   * @property {boolean} isVisible - whether the tooltip is currently visible
   */
  constructor(container) {
    this.container = container;
    this.tooltipContent = this.container.querySelector(
      ".tooltip-info__content"
    );
    this.isVisible = false;
    this._init();
  }

  /**
   * Initialize the tooltip component by adding event listeners for hover, click, or touch interactions.
   *
   * @memberof Tooltip
   * @returns {void}
   */
  _init() {
    if (!this.container || !this.tooltipContent) {
      throw new Error("Tooltip container or content not found");
    }

    const isTouchDevice =
      "ontouchstart" in window || navigator.maxTouchPoints > 0;

    this.container.addEventListener("click", this._toggleTooltip);

    if (!isTouchDevice) {
      this.container.addEventListener("mouseenter", this._showTooltip);
      this.container.addEventListener("mouseleave", this._hideTooltip);
    }

    this.container.addEventListener("focus", this._showTooltip);
    this.container.addEventListener("blur", this._hideTooltip);

    document.addEventListener("click", this._handleOutsideClick);
  }

  /**
   * Shows the tooltip by updating the aria-hidden attribute and setting the isVisible flag to true.
   *
   * @memberof Tooltip
   * @returns {void}
   */
  _showTooltip = () => {
    if (this.isVisible) return;

    this.tooltipContent.setAttribute("aria-hidden", "false");
    this.isVisible = true;
  };

  /**
   * Hides the tooltip by updating the aria-hidden attribute and setting the isVisible flag to false.
   *
   * @memberof Tooltip
   * @returns {void}
   */
  _hideTooltip = () => {
    if (!this.isVisible) return;

    this.tooltipContent.setAttribute("aria-hidden", "true");
    this.isVisible = false;
  };

  /**
   * Toggles the visibility of the tooltip when the icon is clicked.
   *
   * @memberof Tooltip
   * @returns {void}
   */
  _toggleTooltip = () => {
    this.isVisible ? this._hideTooltip() : this._showTooltip();
  };

  /**
   * Handles clicks outside the tooltip, closing it if clicked outside of the tooltip or the tooltip icon.
   *
   * @param {Event} e - The click event object.
   * @memberof Tooltip
   * @returns {void}
   */
  _handleOutsideClick = (e) => {
    const icon = this.container.querySelector(".tooltip-info__icon");

    if (
      this.isVisible &&
      !this.container.contains(e.target) &&
      e.target !== icon
    ) {
      this._hideTooltip();
    }
  };
}
